import { all, call, delay, put, race, takeEvery, takeLatest } from 'redux-saga/effects';
import { authActions } from './authSlice';
import { desksActions } from 'store/desksSlice/desksSlice';
import { sendLocalStorageSignal } from '../../utils/sessionStorageHandlers';
import { store } from 'store/store';
import { aiqLogger } from 'services/aiqLogger';
import { axiosAiq } from 'config';
import { notificationHandler } from 'utils/utils';
import { axiosSSO } from 'config';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { GENERAL } from 'config';

import { sharedWorkerActions, sharedWorker } from 'workers/sharedWorker/utils/utils';

const LOGOUT_LOG_SEND_TIMEOUT_MS = 2000;

export function* getUserData() {
    let hasUser = false;
    try {
        const response = yield axiosAiq('/auth');
        const { username, roles, id, streamManagerToken } = response?.data || {};

        if (roles?.length) {
            const user = { username, roles, id };
            hasUser = true;
            yield put(authActions.setUser(user));
            yield put(authActions.setStreamManagerToken(streamManagerToken));
        }
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Error getting user data!',
        });
    }
    // With a user, syncLogAuthor (on setUser) sets the author of the logs. Without one (no session, or /auth failed
    // for any reason) the logs written so far are settled as unknown right away: the app then redirects to SSO
    // (ProtectedRoute -> Redirect), so this page is left at once and no user would ever arrive for them - waiting
    // would only delay them by PENDING_AUTHOR_MAX_AGE_MS.
    if (!hasUser) yield call([aiqLogger, aiqLogger.settleUnknownAuthor]);
    yield put(authActions.setIsUserDataRequested(true));
}

export function* logout() {
    const { streamManagerToken = '' } = store.getState().auth ?? {};
    try {
        sharedWorkerActions.removeWindow({ id: sharedWorker.id });
        const webRtcEndpointIds = cameraStreamManager.getWebRTCEndpointIds();
        if (webRtcEndpointIds.length) {
            console.log(`logout(): Sending WebRTCEndpointId list for releasing ids=[${webRtcEndpointIds}]`);
            const sent = navigator.sendBeacon(
                `${GENERAL.ADDRESSES.HTTP.MAIN}/camera-stream/release-endpoints`,
                JSON.stringify({ webRtcEndpointIds, streamManagerToken })
            );
            console.log(`logout(): WebRTCEndpointIds ${sent ? '' : 'NOT '}sent!`);
        } else {
            console.log(`No WebRTCEndpointIds for release`, webRtcEndpointIds);
        }

        // Before the session ends and the user is cleared: the last logs are stored under this user and sent
        // while the session is still valid. Capped so a slow or unreachable /logs never delays the logout.
        yield race([call([aiqLogger, aiqLogger.flushBeforeLogout]), delay(LOGOUT_LOG_SEND_TIMEOUT_MS)]);
        yield call(axiosSSO.post, '/logout');
        yield put(authActions.setUser(null));
        sessionStorage.clear();
        sendLocalStorageSignal('AIQ_UserLoggedOut');
        yield put(desksActions.setCurrentDesk({}));
        yield put(desksActions.fetchDesksSuccess([]));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: 'Logout error.',
        });
    }
}

/**
 * Keeps the author of the logs in step with the store, whatever sets the user (/auth, a session handed over by
 * another tab). setUser(null) on logout passes no user, which leaves the author as it was (see setUsername).
 */
export function* syncLogAuthor({ payload: user }) {
    yield call([aiqLogger, aiqLogger.setUsername], user?.username);
}

function* onSetUser() {
    yield takeEvery(authActions.setUser.type, syncLogAuthor);
}

function* onGetUserData() {
    yield takeLatest(authActions.getUserData.type, getUserData);
}

function* onLogoutStart() {
    yield takeLatest(authActions.logoutStart.type, logout);
}

export function* authSaga() {
    yield all([call(onSetUser), call(onGetUserData), call(onLogoutStart)]);
}
